import React, { useState } from 'react';
import { X } from 'lucide-react';
import Switch from '../common/Switch';
import { Configuracion } from '../../types';

interface PortalPoliciesModalProps {
    config: Configuracion;
    onClose: () => void;
    onSave: (updatedConfig: Configuracion) => void;
}

// Edits the client-portal policies + notification switch for a center. Kept
// SEPARATE from the tarifas modal on purpose: saving here does a plain config
// update (no tarifa bulk-recalculation flow).
const PortalPoliciesModal: React.FC<PortalPoliciesModalProps> = ({ config, onClose, onSave }) => {
    const [form, setForm] = useState({
        minCancellationHours: config.minCancellationHours,
        cancellationHorizonDays: config.cancellationHorizonDays,
        rescheduleDays: config.rescheduleDays,
        lastWeekCarryover: config.lastWeekCarryover !== false,
        visibleMonths: config.visibleMonths,
        notificationsEnabled: config.notificationsEnabled !== false,
        invitePortalOnCreate: config.invitePortalOnCreate !== false,
        waitlistMode: config.waitlistMode ?? 'off',
        waitlistCutoffMinutes: config.waitlistCutoffMinutes,
        // P1 — `=== true` y no `!== false`: esta bandera nace apagada, así que un
        // centro que nunca la ha tocado tiene que leerse como apagada.
        recoveryAnyLevel: config.recoveryAnyLevel === true,
    });

    const handleNumber = (
        name: 'minCancellationHours' | 'rescheduleDays' | 'cancellationHorizonDays' | 'visibleMonths'
            | 'waitlistCutoffMinutes',
        raw: string,
    ) => {
        // Empty horizon = "no limit" (null); empty for the rest leaves it unset (backend default).
        const value = raw === '' ? (name === 'cancellationHorizonDays' ? null : undefined) : Number(raw);
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleSave = () => {
        onSave({ ...config, ...form });
        onClose();
    };

    return (
        // `items-start` y no `items-center`: centrando, cuando el contenido es más
        // alto que la ventana el desbordamiento se reparte arriba y abajo, y la
        // parte de arriba queda FUERA DE ALCANCE por mucho que se haga scroll.
        // Además el cuerpo scrollea por su cuenta, para que cabecera y pie se
        // queden siempre visibles.
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-start overflow-y-auto h-full w-full p-4 z-50">
            <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl mx-auto my-auto flex flex-col max-h-[calc(100vh-2rem)]">
                {/* Header */}
                <div className="flex justify-between items-center p-6 border-b shrink-0">
                    <h2 className="text-xl font-bold text-gray-800">Políticas del portal de clientes</h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                        <X size={20} />
                    </button>
                </div>

                <div className="p-6 overflow-y-auto">
                    <p className="text-xs text-gray-500 mb-4">
                        El cliente <strong>siempre puede cancelar</strong> su clase mientras no haya empezado.
                        Estas reglas deciden si esa cancelación le da derecho a una <strong>recuperación</strong>.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="text-xs font-medium text-gray-600 mb-1 block">
                                Horas mínimas de antelación para tener recuperación
                            </label>
                            <input
                                type="number"
                                min="0"
                                value={form.minCancellationHours ?? ''}
                                onChange={(e) => handleNumber('minCancellationHours', e.target.value)}
                                className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="24"
                            />
                            <p className="text-[11px] text-gray-400 mt-1">
                                Cancelando con menos antelación, la clase se pierde.
                            </p>
                        </div>
                        <div>
                            <label className="text-xs font-medium text-gray-600 mb-1 block">
                                Días máx. de antelación con recuperación
                            </label>
                            <input
                                type="number"
                                min="0"
                                value={form.cancellationHorizonDays ?? ''}
                                onChange={(e) => handleNumber('cancellationHorizonDays', e.target.value)}
                                className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="Sin límite"
                            />
                            <p className="text-[11px] text-gray-400 mt-1">Vacío = sin límite</p>
                        </div>
                    </div>

                    {/* Qué parte del calendario ve el cliente */}
                    <div className="mt-6 border-t pt-4">
                        <p className="text-sm font-medium text-gray-700 mb-1">Calendario del cliente</p>
                        <div className="max-w-xs">
                            <label className="text-xs font-medium text-gray-600 mb-1 block">
                                Meses visibles (contando el actual)
                            </label>
                            <input
                                type="number"
                                min="1"
                                value={form.visibleMonths ?? ''}
                                onChange={(e) => handleNumber('visibleMonths', e.target.value)}
                                className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="1"
                            />
                            <p className="text-[11px] text-gray-400 mt-1">
                                1 = solo lo que queda de este mes. 2 = este mes y el siguiente. El cliente sigue
                                inscrito el resto del año; simplemente no lo ve todo de golpe.
                            </p>
                        </div>
                    </div>

                    {/* Ventana para gastar la recuperación */}
                    <div className="mt-6 border-t pt-4">
                        <p className="text-sm font-medium text-gray-700 mb-1">Plazo para usar una recuperación</p>
                        <p className="text-xs text-gray-500 mb-3">
                            Por norma, una recuperación se gasta <strong>dentro del mismo mes</strong> de la clase
                            cancelada: caduca el último día del mes. La excepción es la última semana, que se
                            quedaría sin margen.
                        </p>

                        <Switch
                            checked={form.lastWeekCarryover}
                            onChange={(v) => setForm((prev) => ({ ...prev, lastWeekCarryover: v }))}
                            label="Las clases de la última semana del mes pueden recuperarse el mes siguiente"
                            description="Última semana = del último lunes del mes hasta el día 30 o 31. Desactivado, también caducan a fin de mes."
                        />

                        {form.lastWeekCarryover && (
                            <div className="mt-3 ml-6 max-w-xs">
                                <label className="text-xs font-medium text-gray-600 mb-1 block">
                                    Días para recuperarla, en ese caso
                                </label>
                                <input
                                    type="number"
                                    min="0"
                                    value={form.rescheduleDays ?? ''}
                                    onChange={(e) => handleNumber('rescheduleDays', e.target.value)}
                                    className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="15"
                                />
                                <p className="text-[11px] text-gray-400 mt-1">
                                    Contados desde la fecha de la clase cancelada.
                                </p>
                            </div>
                        )}
                    </div>

                    {/* P1 — en qué niveles se puede gastar una recuperación */}
                    <div className="mt-6 border-t pt-4">
                        <p className="text-sm font-medium text-gray-700 mb-1">Niveles en los que se puede recuperar</p>

                        <Switch
                            checked={form.recoveryAnyLevel}
                            onChange={(v) => setForm((prev) => ({ ...prev, recoveryAnyLevel: v }))}
                            label="Permitir recuperar en clases de cualquier nivel"
                            description="Desactivado, el cliente solo puede recuperar en los niveles en los que está inscrito (y en el de la clase que canceló, para que la recuperación no se quede atrapada si pierde esa plaza)."
                        />

                        {form.recoveryAnyLevel && (
                            <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mt-3">
                                El aforo sigue saliendo de la sala, así que un cliente de un nivel puede ocupar la
                                última plaza de otro. Afecta también a la lista de espera: se podrá apuntar a clases
                                completas de cualquier nivel.
                            </p>
                        )}
                    </div>

                    {/* D1 — lista de espera de las clases completas */}
                    <div className="mt-6 border-t pt-4">
                        <p className="text-sm font-medium text-gray-700 mb-1">Lista de espera</p>
                        <p className="text-xs text-gray-500 mb-3">
                            Cuando una clase está completa, el cliente puede apuntarse a su lista de espera.
                            Para apuntarse necesita una <strong>recuperación válida</strong> para esa clase, y
                            al conseguir la plaza se le registra.
                        </p>

                        <div className="max-w-sm">
                            <label className="text-xs font-medium text-gray-600 mb-1 block">
                                Cómo se reparte una plaza que se libera
                            </label>
                            <select
                                value={form.waitlistMode}
                                onChange={(e) => setForm((prev) => ({
                                    ...prev,
                                    waitlistMode: e.target.value as 'off' | 'auto' | 'manual',
                                }))}
                                className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="off">Desactivada</option>
                                <option value="auto">Automática — se inscribe al primero de la lista</option>
                                <option value="manual">Por orden de reserva — se avisa a toda la lista</option>
                            </select>
                            <p className="text-[11px] text-gray-400 mt-1">
                                {form.waitlistMode === 'auto'
                                    ? 'Al liberarse una plaza se inscribe automáticamente al primero de la cola que tenga una recuperación válida, y se le avisa por email. No tiene que hacer nada.'
                                    : form.waitlistMode === 'manual'
                                        ? 'Al liberarse una plaza se avisa por email a toda la lista y la plaza es para quien la reserve antes. Los demás siguen en la lista.'
                                        : 'Los clientes no ven la lista de espera y no pueden apuntarse.'}
                            </p>
                        </div>

                        {form.waitlistMode !== 'off' && (
                            <div className="mt-3 max-w-xs">
                                <label className="text-xs font-medium text-gray-600 mb-1 block">
                                    Minutos antes de la clase en que se cierra
                                </label>
                                <input
                                    type="number"
                                    min="0"
                                    value={form.waitlistCutoffMinutes ?? ''}
                                    onChange={(e) => handleNumber('waitlistCutoffMinutes', e.target.value)}
                                    className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="30"
                                />
                                <p className="text-[11px] text-gray-400 mt-1">
                                    A partir de ahí ya no se puede entrar en la lista ni se reparten plazas: no
                                    daría tiempo a avisar a nadie.
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Notificaciones por email */}
                    <div className="mt-6 border-t pt-4">
                        <Switch
                            checked={form.notificationsEnabled}
                            onChange={(v) => setForm((prev) => ({ ...prev, notificationsEnabled: v }))}
                            label="Enviar notificaciones por email a los clientes"
                            description="Reservas, cancelaciones, recuperaciones y cambios de plaza. Desactívalo para silenciar todos los emails de este centro."
                        />
                    </div>

                    {/* Invitación al portal en el alta */}
                    <div className="mt-6 border-t pt-4">
                        <Switch
                            checked={form.invitePortalOnCreate}
                            onChange={(v) => setForm((prev) => ({ ...prev, invitePortalOnCreate: v }))}
                            label="Invitar al portal al dar de alta un cliente"
                            description="Cómo aparece marcada la casilla «Invitar al portal» en la inscripción. Se puede cambiar en cada alta. Los clientes sin correo nunca se invitan."
                        />
                    </div>
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-3 p-6 border-t shrink-0">
                    <button onClick={onClose} className="px-4 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded">
                        Cancelar
                    </button>
                    <button onClick={handleSave} className="px-4 py-2 bg-brand-primary hover:brightness-90 text-white rounded">
                        Guardar
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PortalPoliciesModal;
